export type AgentIntent =
  | 'JSON_CANVAS_GEN'
  | 'DELTA_PATCH'
  | 'DEPENDENCY_UPDATE'
  | 'UI_MORPH'
  | 'SECURITY_GUARD'
  | 'ECONOMY_LOOP'

export type RouterDecision = 'ROUTE' | 'CLARIFY' | 'REJECT' | 'INTERVIEW_MODE'
export type AiOperatingMode = 'CREATE' | 'EXECUTE'

/**
 * User-selected quality budget for the AI Magic composer. Orthogonal to
 * AiOperatingMode. See docs/pricing/line-tier-plan.md §2 for the backing
 * models and docs/pricing/line-tier-integration-plan.md §3 for the state
 * shape. `line` = Vertex Flash · `line_pro` = Sonnet 4.5 · `line_max` = Opus 4.7.
 */
export type AiTierId = 'line' | 'line_pro' | 'line_max'

export const DEFAULT_AI_TIER: AiTierId = 'line'

/**
 * Full model catalogue the tier system may pick.
 *
 * Two-part shape so runtime code and static types both stay honest:
 *   - Named literals cover every model in src/config/aiModelRegistry.json today.
 *     Adding one below without adding it to the JSON, or vice versa, is a bug.
 *   - The `string & {}` tail lets callers pass an id they read from the
 *     registry at runtime without a cast, while still autocompleting the
 *     known set. See docs/pricing/line-tier-integration-plan.md Phase J.
 */
export type AiModelId =
  | 'gemini-2.5-flash'
  | 'gemini-2.5-pro'
  | 'gemini-3.7-flash'
  | 'gemini-3.7-pro'
  | 'claude-sonnet-4-5'
  | 'claude-sonnet-5'
  | 'claude-opus-4-5'
  | 'claude-opus-4-7'
  | 'claude-opus-4-8'
  | 'claude-fable-5'
  | (string & {})

/** Legacy session strings — fold to the CREATE/EXECUTE surface on read. */
export type LegacyAiOperatingMode = 'PLANNING' | 'EXECUTION' | 'ACTION'

export const mapLegacyOperatingMode = (
  value: AiOperatingMode | LegacyAiOperatingMode | string | undefined | null,
): AiOperatingMode => {
  if (value === 'EXECUTE' || value === 'ACTION') return 'EXECUTE'
  return 'CREATE'
}
export type AIResultStatus = 'CONFIRMED' | 'PENDING_APPROVAL' | 'PENDING_CLARIFICATION' | 'REJECTED'
export const AI_ROUTER_CONFIDENCE_THRESHOLD = 0.6
export const AI_ROUTER_AUTOMATION_THRESHOLD = 0.8

export type ClarificationInputType = 'DATE' | 'NUMBER' | 'TEXT' | 'SELECT'

export interface ClarificationMetadata {
  field: string
  inputType: ClarificationInputType
  label: string
  options?: string[]
  min?: number
  max?: number
  step?: number
  /**
   * Multi-question interview payload. When present, the composer renders the
   * new premium InterviewQuestionSet card instead of the legacy chip strip.
   * See src/services/ai/interview/aiMagicQuestionSet.ts. Optional to keep the
   * pre-questionSet chat history readable — legacy turns just skip this field.
   */
  questionSet?: import('../services/ai/interview/aiMagicQuestionSet').AiMagicQuestionSet
}

export interface ProactiveSuggestionPayload {
  id: string
  kind: 'GAP_ANALYSIS' | 'CONTEXT_LINKER'
  message: string
  acceptancePrompt: string
}

export interface SuggestionAcceptancePayload {
  suggestionId: string
  kind: ProactiveSuggestionPayload['kind']
  explicitResponse: string
}

export interface RoutingPayload {
  decision: RouterDecision
  intent?: AgentIntent
  confidenceScore?: number
  message?: string
  /** Brief auditable rationale only; never private chain-of-thought. */
  reasoning: string
  model?: AiModelId
  tier?: 'INSTANT' | 'STRUCTURAL'
  /** User-selected quality budget frozen at send time. Populated post-rollout. */
  tierId?: AiTierId
  clarification?: ClarificationMetadata
  /**
   * Internal routing telemetry — surfaces aiService's PLANNING/EXECUTION/ACTION
   * strings, NOT the user-facing CREATE/EXECUTE tabs. Kept as `string` so server
   * internals don't have to import the public AiOperatingMode alias.
   */
  operatingMode?: string
  interviewMode?: boolean
  requiresUserApproval?: boolean
}

export interface CanvasGenAction {
  type: 'JSON_CANVAS_GEN'
  previewId: string
}

export type DeltaPatchFields = Partial<{
  title: string
  text: string
  description: string
  startDate: string
  endDate: string
  color: string
  baseColor: string
  weight: number
  isCompleted: boolean
}>

export interface DeltaPatchAction {
  type: 'DELTA_PATCH'
  targetId: string
  targetType: 'GOAL' | 'TASK' | 'SUBTASK' | 'CHECKLIST_ITEM'
  patch: DeltaPatchFields
  preview: string
}

export interface DependencyUpdateAction {
  type: 'DEPENDENCY_UPDATE'
  targetChecklistItemId: string
  prerequisiteIds: string[]
  preview: string
}

export interface UiMorphAction {
  type: 'UI_MORPH'
  command: 'SET_BOARD_FILTER' | 'SET_VIEW_DENSITY' | 'TOGGLE_THEME'
  value?: string
}

export interface SecurityGuardAction {
  type: 'SECURITY_GUARD'
  summary: string
}

export interface EconomyLoopAction {
  type: 'ECONOMY_LOOP'
  operation: 'CREDIT' | 'DEBIT' | 'TRANSFER'
  amount: number
  preview: string
}

export type ActionPayload =
  | CanvasGenAction
  | DeltaPatchAction
  | DependencyUpdateAction
  | UiMorphAction
  | SecurityGuardAction
  | EconomyLoopAction

export interface AIResult<T> {
  status: AIResultStatus
  data: T
  softWarnings: string[]
  confidence: number
  routerConfidence: number
  resultConfidence: number
  reasoning: string[]
  model: AiModelId
  routing: RoutingPayload
  action?: ActionPayload
  actions?: ActionPayload[]
  suggestions?: ProactiveSuggestionPayload[]
  message?: string
  /** True only after the user-facing Vertex request resolved successfully. */
  modelResponseReceived?: boolean
}

export type ResultWrapper<T> = AIResult<T>
